import { useEffect, useRef } from 'react';
import type { WorkflowDocument } from '@/lib/services/correspondences/workflows/workflow-types';

interface UseDraftAttachmentSyncProps {
  localDraftAttachments: WorkflowDocument[];
  pendingAttachments: WorkflowDocument[];
  setPendingAttachments: (attachments: WorkflowDocument[]) => void;
  saveAttachments: (attachments: WorkflowDocument[]) => void;
  currentDraftId: string | null;
  onSaveDraftAttachments?: (
    draftId: number,
    attachments: WorkflowDocument[]
  ) => Promise<void>;
}

/**
 * Manages attachment synchronisation across localStorage and backend draft.
 * - Restores locally-saved attachments on mount
 * - Persists attachment changes to localStorage
 * - Debounce-persists attachment changes to the backend draft
 */
export function useDraftAttachmentSync({
  localDraftAttachments,
  pendingAttachments,
  setPendingAttachments,
  saveAttachments,
  currentDraftId,
  onSaveDraftAttachments
}: UseDraftAttachmentSyncProps) {
  const hasInitializedRef = useRef(false);
  const isFirstRenderRef = useRef(true);
  const saveDraftAttachmentsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Restore locally-saved attachments on mount
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      if (localDraftAttachments.length > 0) {
        setPendingAttachments(localDraftAttachments);
      }
    }
  }, [localDraftAttachments, setPendingAttachments]);

  // Persist attachments to localStorage when they change (skip initial mount)
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    saveAttachments(pendingAttachments);
  }, [pendingAttachments, saveAttachments]);

  // Persist attachments to backend draft when they change (debounced, skip initial mount)
  useEffect(() => {
    if (isFirstRenderRef.current) return;

    const draftId = currentDraftId;
    if (!draftId || !onSaveDraftAttachments) return;

    if (saveDraftAttachmentsTimeoutRef.current) {
      clearTimeout(saveDraftAttachmentsTimeoutRef.current);
    }
    saveDraftAttachmentsTimeoutRef.current = setTimeout(() => {
      onSaveDraftAttachments(parseInt(draftId), pendingAttachments);
    }, 1000);

    return () => {
      if (saveDraftAttachmentsTimeoutRef.current) {
        clearTimeout(saveDraftAttachmentsTimeoutRef.current);
      }
    };
  }, [pendingAttachments, currentDraftId, onSaveDraftAttachments]);
}
